// Where the locale comes from — and it is not the same question on both surfaces.
//
// The estate answered this once, for the marketing site (ADR-0003: one URL, every
// locale in the document, switched client-side). Its reasoning splits on **organic
// search**, and it names the split itself: sites that compete for search in several
// languages give each language its own URL, because the URL is what ranks; product
// UIs commonly serve one URL and switch in the page.
//
// Riksu's two surfaces sit on opposite sides of that split, for the same reason they
// are two bundles:
//
// - **`app` is never indexed.** There is no SEO argument, so there is no argument for
//   a segment in the URL either. One address, and the language is the person's
//   preference. This is ADR-0003's shape and inherits its reasoning wholesale.
//
// - **`careers` must be indexed.** A Turkish query should meet a Turkish `<title>`,
//   and that is only possible if the Turkish page has its own address. A job posting
//   nobody can find is not a posting — which is the whole reason this surface exists
//   as a separate bundle in the first place.
//
// So: `/careers/{handle}` is the SOURCE LANGUAGE and `/{locale}/careers/{handle}` is
// every other one, rather than one address that changes under the reader.
//
// English is unprefixed because English is the source. Turkish is unprefixed no longer,
// and the reasoning that used to put it there — "the default and the majority of this
// market" — is answered rather than discarded: the SEO argument was that a Turkish
// query must meet a Turkish `<title>`, and `/tr/careers/{handle}` gives it exactly that,
// its own address with its own title. What it no longer gets is the unprefixed one, and
// nothing about ranking turns on which language holds the bare path.
//
// The BFF already routes both shapes — `is_careers` matches `["careers", ..]` and
// `[<two letters>, "careers", ..]` — so this costs no server change.

import { asLocale, DEFAULT_LOCALE, type Locale } from "./index";

/** Where the product surface remembers what somebody chose. */
const PREFERENCE = "riksu.locale";

/**
 * The product surface's locale.
 *
 * Order: what they chose, then what their browser asks for, then the source language.
 * The browser's preference is consulted **before** the default so a first visit is
 * already right for most people — a Turkish browser still arrives in Turkish and has
 * nothing to correct — and the stored choice wins over it so a person who corrected it
 * once is not corrected back on every visit.
 *
 * The default is what an UNMATCHED browser gets, and an unmatched browser is one riksu
 * knows nothing about. Serving it the language the product is written in is a smaller
 * claim than guessing at a market from a header that named neither.
 *
 * Every branch here is reachable and every one is CHOOSABLE: `rememberLocale` has a
 * caller now (`ui/language.tsx`), so a reader who disagrees with any of the three can
 * say so once and be obeyed afterwards.
 */
export function appLocale(): Locale {
  const stored = asLocale(read(PREFERENCE));
  if (stored) {
    return stored;
  }
  if (typeof navigator !== "undefined") {
    for (const candidate of navigator.languages ?? [navigator.language]) {
      const matched = asLocale(candidate);
      if (matched) {
        return matched;
      }
    }
  }
  return DEFAULT_LOCALE;
}

/** Records a choice. Silent on failure: private mode is not a reason to refuse. */
export function rememberLocale(locale: Locale): void {
  try {
    localStorage.setItem(PREFERENCE, locale);
  } catch {
    // The choice lasts for this page instead of forever, which is a smaller loss
    // than an exception thrown out of a language switcher.
  }
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/**
 * The public surface's locale, **from the path**.
 *
 * `/careers/acme` is English and `/tr/careers/acme` is Turkish. The source language is
 * unprefixed — the alternative, prefixing both, gives every reader a redundant segment
 * to carry forever, and the alternative to THAT is choosing a market to privilege in
 * the URL, which is the decision this product has just made the other way.
 *
 * The browser's `Accept-Language` is deliberately **not** consulted here. Serving
 * different content at one address by header means a crawler sees whichever language
 * it happened to ask for, and a CDN either caches the wrong one or gives up caching.
 * The URL requested is the URL served.
 */
export function careersLocale(pathname: string): Locale {
  const first = pathname.split("/").filter(Boolean)[0];
  return asLocale(first ?? null) ?? DEFAULT_LOCALE;
}

/**
 * The path with the locale segment removed, so routing sees one shape.
 *
 * Every reader of the path goes through this. It existed with no caller for long enough
 * that `careers.tsx` grew its own `parts[0] === "careers"` test, which is true at
 * `/careers/acme` and false at `/en/careers/acme` — so the prefixed half of the one
 * surface that has to be indexed answered "this address does not name a company" to
 * everybody who reached it. A helper nobody calls is a rule nobody follows.
 */
export function withoutLocale(pathname: string): string {
  const parts = pathname.split("/").filter(Boolean);
  return asLocale(parts[0] ?? null) ? `/${parts.slice(1).join("/")}` : pathname;
}

/** The same page in another locale, for a `<link rel="alternate" hreflang>`. */
export function careersPathIn(locale: Locale, pathname: string): string {
  const bare = withoutLocale(pathname);
  return locale === DEFAULT_LOCALE ? bare : `/${locale}${bare}`;
}
